<template>
  <div v-if="visible" class="debug-modal-backdrop" role="dialog" aria-modal="true" @click.self="close">
   <div class="debug-modal">
      <div class="modal-header">
        <h3 class="modal-title">Depuración de Tablas - Logs de Usuarios</h3>
        <button
          type="button"
          class="btn-close"
          @click="close"
          aria-label="Cerrar"
        >
          <span class="material-icons close-icon" aria-hidden="true">close</span>
        </button>
      </div>

      <section class="debug-modal-body">
        <div class="debug-modal-row">
          <label class="debug-modal-label">Fecha desde</label>
          <input type="date" v-model="dateFrom" class="debug-input" />
        </div>
        <div class="debug-modal-row">
          <label class="debug-modal-label">Fecha hasta</label>
          <input type="date" v-model="dateTo" class="debug-input" />
        </div>
      </section>

      <footer class="debug-modal-footer">
        <button type="button" @click="close" class="btn btn-secondary">Cancelar</button>
        <button type="button" @click="confirmPurge" class="btn btn-danger">Depurar Logs</button>
      </footer>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import Swal from 'sweetalert2';

export default {
  name: 'PurgeUserLogsModal',
  props: { visible: { type: Boolean, default: false } },
  data() {
    return {
      dateFrom: '',
      dateTo: '',
      dateColumn: 'inicio_sesion' // por defecto
    };
  },
  mounted() {
    window.addEventListener('keydown', this._onKeydown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this._onKeydown);
  },
  methods: {
    close() {
      // reset campos antes de cerrar
      this.dateFrom = '';
      this.dateTo = '';
      this.$emit('update:visible', false);
    },
    _onKeydown(e) {
      if (e.key === 'Escape' && this.visible) {
        this.close();
      }
    },
    getAuthHeaders() {
      const tok = localStorage.getItem('api_token') || localStorage.getItem('token') || null;
      if (tok) return { Authorization: 'Bearer ' + tok, Accept: 'application/json' };
      return {};
    },
    async confirmPurge() {
      // validaciones básicas de fechas
      if (!this.dateFrom || !this.dateTo) {
        await Swal.fire({ title: 'Aviso', text: 'Debes seleccionar ambas fechas.', icon: 'warning', confirmButtonColor: '#99172A' });
        return;
      }
      const from = new Date(this.dateFrom);
      const to = new Date(this.dateTo);
      if (from > to) {
        await Swal.fire({ title: 'Fechas inválidas', text: 'La fecha de inicio debe ser anterior a la de fin.', icon: 'error', confirmButtonColor: '#99172A' });
        return;
      }

      // Solicitar keyword para mayor seguridad
      const KEYWORD = 'ELIMINAR';
      const result = await Swal.fire({
        title: 'Confirmar borrado de logs (IRREVERSIBLE)',
        html: `
          <p>Se eliminarán logs de usuarios entre <strong>${this.dateFrom}</strong> y <strong>${this.dateTo}</strong>. Esta acción es <strong style="color:#b12b34;">IRREVERSIBLE</strong>.</p>
          <p>Escribe <strong>${KEYWORD}</strong> para confirmar el borrado.</p>
        `,
        icon: 'warning',
        input: 'text',
        inputPlaceholder: KEYWORD,
        showCancelButton: true,
        confirmButtonText: 'Borrar (IRREVERSIBLE)',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#b12b34',
        reverseButtons: true,
        inputValidator: (value) => {
          if (value === KEYWORD) return null;
          return `Debes escribir "${KEYWORD}" para confirmar.`;
        }
      });
      if (!result.isConfirmed) return;

      const body = { dateFrom: this.dateFrom, dateTo: this.dateTo, dateColumn: this.dateColumn, confirm: true };
      try {
        const headers = this.getAuthHeaders();
        const res = await api.post('/debug/purge-user-logs', body, { headers });
        await Swal.fire({ title: 'Hecho', text: `Registros borrados: ${res.data.deleted ?? res.data.deleted_count ?? 0}`, icon: 'success', confirmButtonColor: '#99172A' });
        this.$emit('done', res.data);
        this.close();
      } catch (err) {
        const msg = err.response && err.response.data ? JSON.stringify(err.response.data, null, 2) : String(err);
        await Swal.fire({ title: 'Error', text: msg, icon: 'error', confirmButtonColor: '#99172A' });
      }
    }
  }
};
</script>

<style scoped>
/* Reutiliza estilo de los otros modales */
.debug-modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1200; }

/* Modal: centrado, altura automática según contenido y scroll interno cuando sea necesario */
.debug-modal {
  background-color: #fff;
  border-radius: 12px;
  width: 700px;
  max-width: 95vw;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  overflow: auto;                /* permite scroll interno cuando el contenido es alto */
  display: flex;
  flex-direction: column;
  position: relative;            /* centrar dentro del backdrop */
  margin: 24px;                  /* espacio alrededor para que no toque los bordes */
  max-height: 80vh;              /* limita la altura del modal a la pantalla */
}

/* cabecera (sticky dentro del contenedor scrollable) */
.modal-header {
  display: flex;
  align-items: center;          /* centra verticalmente h3 y botón */
  justify-content: center;      /* mantiene el título centrado */
  position: relative;           /* para posicionar el botón sin afectar el flujo */
  background-color: #99172A;
  color: #fff;
  width: 100%;
  box-sizing: border-box;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  padding: 12px 48px;           /* espacio lateral uniforme */
  position: sticky;
  top: 0;
  z-index: 20;
}

.modal-title {
  margin: 0;
  font-size: 1.6rem;
  font-weight: bold;
  text-align: center;
  line-height: 1;
  padding: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* botón de cierre: posición absoluta dentro del header, centrado verticalmente */
.btn-close {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);  /* centra verticalmente respecto al header */
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #fff;
}
.btn-close .close-icon {
  font-size: 20px;
  color: #fff;
}

.debug-modal-body { display: flex; gap: 12px; margin-top: 8px; padding: 12px; }
.debug-modal-row { display: flex; flex-direction: column; flex: 1; }
.debug-modal-label { font-size: 13px; margin-bottom: 6px; color: #333; }
.debug-input { padding: 8px; border: 1px solid #dcdcdc; border-radius: 4px; }

.debug-modal-footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px; padding: 12px; }
.btn { padding: 8px 12px; border-radius: 4px; border: none; cursor: pointer; }
.btn-secondary { background: #e9ecef; color: #212529; }
.btn-danger { background: #b12b34; color: #fff; }

.text-danger { color: #b12b34; }
</style>